import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { Icon, TextField } from "@o4c/plugin-components";
import { OMRPResource } from "../../../interfaces/resource.interface";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { DropdownPortal } from "../dropdown-portal/dropdown-portal";

import styles from "./resource-search-bar.module.scss";

interface IResourceSearchBarProps {
    resources: OMRPResource[];
    onSelect: (resourceName: string | null) => void;
}

export const ResourceSearchBar: FunctionComponent<IResourceSearchBarProps> = (
    props: IResourceSearchBarProps
) => {
    const { resources, onSelect } = props;
    const { t } = useTranslation();

    const [inputValue, setInputValue] = useState<string>("");
    const [selectedResource, setSelectedResource] = useState<string | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);

    const filteredOptions = useMemo<string[]>(() => {
        if (!inputValue) return [];
        const lower = inputValue.toLowerCase();
        const names = _.uniq(resources.map((r) => r.displayName)).sort();
        return names.filter((name) => name.toLowerCase().includes(lower));
    }, [inputValue, resources]);

    // Close dropdown on outside click (check both container and portal dropdown)
    useEffect(() => {
        const handleMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            const inContainer = containerRef.current?.contains(target);
            const inDropdown = dropdownRef.current?.contains(target);
            if (!inContainer && !inDropdown) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, []);

    // Close dropdown when parent scrolls (fixed dropdown would be mispositioned)
    useEffect(() => {
        if (!isDropdownOpen) return;

        const scrollContainer = containerRef.current?.closest(".team-table-container");
        if (!scrollContainer) return;

        const handleScroll = () => setIsDropdownOpen(false);
        scrollContainer.addEventListener("scroll", handleScroll);
        return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }, [isDropdownOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setIsDropdownOpen(value.length > 0);
    };

    const handleSelect = (name: string) => {
        setSelectedResource(name);
        setInputValue(name);
        setIsDropdownOpen(false);
        onSelect(name);
    };

    const handleClear = () => {
        setSelectedResource(null);
        setInputValue("");
        setIsDropdownOpen(false);
        onSelect(null);
        inputRef.current?.focus();
    };

    const handleClearInputValue = () => {
        setInputValue("");
        setIsDropdownOpen(false);
        inputRef.current?.focus();
    };

    if (selectedResource) {
        return (
            <div className={styles.searchBarContainer} ref={containerRef}>
                <div className={styles.selectedState}>
                    <div className={styles.searchIconWrapper}>
                        <Icon name="search" size="small" />
                    </div>
                    <div className={styles.selectedName}>{selectedResource}</div>
                    <button
                        className={`${styles.clearButton} ${styles.primary}`}
                        onClick={handleClear}
                    >
                        <Icon name="close" size="small" color="primary" />
                        <span>{t("teamSchedule.clear")}</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.searchBarContainer} ref={containerRef}>
            <TextField
                ref={inputRef}
                name="resource-search"
                className={styles.searchInput}
                type="text"
                value={inputValue}
                onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>)}
                placeholder={t("teamSchedule.searchResources")}
                onFocus={() => {
                    if (inputValue.length > 0) setIsDropdownOpen(true);
                }}
                icon={
                    <>
                        {inputValue && (
                            <button
                                className={styles.clearButton}
                                onClick={handleClearInputValue}
                            >
                                <Icon name="close" size="small" />
                            </button>
                        )}
                    </>
                }
            />
            {isDropdownOpen && !_.isEmpty(filteredOptions) && containerRef.current && (
                <DropdownPortal
                    ref={dropdownRef}
                    options={filteredOptions}
                    inputValue={inputValue}
                    anchorRect={containerRef.current.getBoundingClientRect()}
                    onSelect={handleSelect}
                />
            )}
        </div>
    );
};
